// src/modules/requerimiento/components/devolucion/DevueltosComponents.jsx
// Piezas de devolución que comparten Trabajo de campo y Revisión en Sistemas: un equipo
// devuelto en una línea, la tarjeta de un chip devuelto y el detalle del equipo con su foto.
import React from "react";
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography,
} from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import SimCardOutlinedIcon from "@mui/icons-material/SimCardOutlined";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ModemImageManager from "../ModemImageManager";
import { Seccion, Grilla, Dato, Observacion } from "../adicion/EquipoInstaladoDialog";

const AZUL_EQUIPOS = "#0284C7";
const AMBAR = "#F59E0B";

/** Un equipo devuelto en una línea: SIM / EMAI, quién lo tenía, foto; clic abre el detalle. */
export function FilaModemDevuelto({ modem, onClick, conPlaca = false }) {
  const sub = [
    conPlaca && modem.placa ? modem.placa : null,
    modem.emai && modem.emai !== modem.num_sim ? modem.emai : null,
    modem.ciudad,
  ].filter(Boolean).join(" · ");
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      onClick={onClick}
      sx={{ px: 1.25, py: 1, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
    >
      <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "rgba(2,132,199,0.12)", color: AZUL_EQUIPOS, display: "grid", placeItems: "center", flexShrink: 0 }}>
        <MemoryIcon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{modem.num_sim || modem.id || "—"}</Typography>
        {sub && <Typography variant="caption" color="text.secondary" noWrap component="div">{sub}</Typography>}
      </Box>
      {!modem.imagen_url && (
        <Typography variant="caption" sx={{ color: AMBAR, fontWeight: 600, flexShrink: 0 }}>Sin foto</Typography>
      )}
      <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
    </Stack>
  );
}

export function ChipDevueltoCard({ chip, onClick }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: "10px" }}>
      <CardContent
        onClick={onClick}
        sx={{ p: 1.5, "&:last-child": { pb: 1.5 }, cursor: onClick ? "pointer" : "default", "&:hover": onClick ? { bgcolor: "action.hover" } : undefined }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "action.hover", color: "text.secondary", display: "grid", placeItems: "center", flexShrink: 0 }}>
            <SimCardOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{chip.numero || "—"}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {[chip.relacion && `Módem ${chip.relacion}`, chip.ciudad].filter(Boolean).join(" · ") || "—"}
            </Typography>
          </Box>
          {chip.estado && <Chip size="small" variant="outlined" label={chip.estado} />}
        </Stack>
        {chip.observacion && <Box sx={{ mt: 1 }}><Observacion texto={chip.observacion} /></Box>}
      </CardContent>
    </Card>
  );
}

export function ModemDevueltoDialog({ modem, cod, toAbsUrl, onClose, onChanged, onToast, onVerEquipo }) {
  const id = modem?.num_sim || modem?.emai || modem?.id;
  return (
    <Dialog open={!!modem} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px" } }}>
      {modem && (
        <>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "8px", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0, background: `linear-gradient(135deg, ${AZUL_EQUIPOS}, #075985)` }}>
              <AssignmentReturnIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>Equipo devuelto</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>{modem.num_sim || modem.id || "—"}</Typography>
            </Box>
            <IconButton size="small" aria-label="Cerrar" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
          <DialogContent dividers sx={{ px: 2.5 }}>
            <Stack spacing={2.5}>
              <Seccion titulo="Devolución">
                <Grilla>
                  <Dato label="Placa" value={modem.placa} />
                  <Dato label="Sucursal" value={modem.ciudad} />
                  <Dato label="Asignado" value={modem.asignado} />
                  <Dato label="Razón social" value={modem.razon_social} />
                </Grilla>
                {modem.observacion && <Observacion texto={modem.observacion} />}
              </Seccion>
              <Seccion titulo="Foto del equipo">
                <ModemImageManager
                  cod={cod}
                  numSim={modem.num_sim || modem.id}
                  initialUrl={modem.imagen_url || ""}
                  toAbsUrl={toAbsUrl}
                  onChanged={onChanged}
                  onToast={onToast}
                />
              </Seccion>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            {id && (
              <Button onClick={() => onVerEquipo(id)} sx={{ textTransform: "none", mr: "auto" }}>
                Ver ficha del equipo
              </Button>
            )}
            <Button onClick={onClose} variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>Cerrar</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
